// Ein Dialog-Host für alle Sperren und Limits. Seiten, Badges und Guards
// öffnen ihn über usePricingDialog, gerendert wird er einmal als
// components/pricing/pricing-dialog.js direkt unter PaymentContextProvider
// (context/index.js).
//
// Zwei Contexts wie in context/sample-data-context.js, damit Seiten mit
// openFeatureDialog nicht bei jedem Öffnen und Schließen neu rendern.
//
// HeadlessUI 2.1 stapelt Geschwister-Dialoge nicht sauber. Wer schon in einem
// Modal oder Slideover sitzt, nimmt UpgradeInline statt dieses Dialogs. Guards
// prüfen vor dem Öffnen eines Modals, damit nie zwei Dialoge offen sind.
//
// Liegt in der Kette von context/index.js und importiert deshalb direkt aus
// den Dateien, nie aus features/pricing/index.js.
import PropTypes from 'prop-types'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'

import trackEvents from '../../constants/track-events'
import { usePaymentContext } from '../../context/payment-context'
import { trackEvent } from '../../helpers/analytics'
import { LEGACY_ENTITLEMENTS, MODES } from './entitlements'
import { FEATURES, LEGACY_FEATURE_KEYS } from './plans'

export const PRICING_DIALOG_VIEWS = Object.freeze({
  FEATURE: 'feature',
  LIMIT: 'limit'
})

export const LIMIT_DIALOG_ACTIONS = Object.freeze({
  ACTIVATE: 'activate',
  RECOVER: 'recover',
  INVITE: 'invite',
  DUPLICATE: 'duplicate',
  DISCOVER: 'discover',
  SELECTION: 'selection',
  INFO: 'info'
})

const LIMIT_DIALOG_RESOURCES = Object.freeze([
  'careers',
  'seats',
  'talentStorage'
])

const PricingDialogActionsContext = createContext(null)
const PricingDialogStateContext = createContext(null)

PricingDialogActionsContext.displayName = 'PricingDialogActionsContext'
PricingDialogStateContext.displayName = 'PricingDialogStateContext'

// Beim Schließen bleiben view, key und context stehen, damit der Inhalt
// während der Ausblende-Animation nicht wechselt
export const CLOSED_PRICING_DIALOG = Object.freeze({
  open: false,
  requestId: 0,
  view: null,
  key: null,
  source: null,
  context: Object.freeze({})
})

const trackingBase = (entitlements) => ({
  plan: entitlements?.plan?.id ?? null,
  mode: entitlements?.mode ?? MODES.LEGACY,
  simulated: !!entitlements?.isSimulated
})

export function PricingDialogProvider({ children }) {
  const [dialog, setDialog] = useState(CLOSED_PRICING_DIALOG)
  const { entitlements, showPaywall } = usePaymentContext()

  // Über Refs, damit die Aktionen stabil bleiben und Konsumenten nicht neu
  // rendern, wenn sich Entitlements ändern
  const latest = useRef({ entitlements, showPaywall })

  useEffect(() => {
    latest.current = { entitlements, showPaywall }
  }, [entitlements, showPaywall])

  /**
   * Upgrade-Dialog für ein Feature. Ohne Key oder mit unbekanntem Key zeigt
   * er die allgemeine Variante.
   *
   * Der neue Dialog öffnet nur in enforced. In legacy und preview öffnen die
   * fünf Bestands-Gates und die allgemeine Variante weiter genau die heutige
   * Paywall. Die neuen Gates sind dort frei, ein Aufruf ist ein Fehler des
   * Aufrufers und bleibt ohne Dialog. So sehen nicht migrierte Kunden nichts
   * Neues, und vor der Freigabe geht keine Anfrage nach #vertrieb, egal was
   * der Aufrufer prüft.
   *
   * @param {string|null} featureKey Key aus FEATURES
   * @param {{ source?: string, inUse?: boolean }} [options] inUse zeigt den
   *   Hinweis, dass Eingerichtetes weiterläuft
   */
  const openFeatureDialog = useCallback(
    (featureKey = null, { source = null, inUse = false } = {}) => {
      const current = latest.current
      const currentEntitlements = current.entitlements ?? LEGACY_ENTITLEMENTS
      const key =
        featureKey && Object.prototype.hasOwnProperty.call(FEATURES, featureKey)
          ? featureKey
          : null

      if (featureKey && !key) {
        console.error(
          new Error(`pricing dialog: unbekannter Key ${String(featureKey)}`)
        )
      }

      if (currentEntitlements.mode !== MODES.ENFORCED) {
        if (!key || LEGACY_FEATURE_KEYS[key]) {
          current.showPaywall?.('general')
          return
        }

        console.error(
          new Error(
            `pricing dialog: ${key} außerhalb von enforced (${currentEntitlements.mode})`
          )
        )
        return
      }

      trackEvent(trackEvents.PRICING_DIALOG_VIEWED, {
        view: PRICING_DIALOG_VIEWS.FEATURE,
        feature: key,
        source,
        in_use: !!inUse,
        ...trackingBase(currentEntitlements)
      })

      setDialog((prev) => ({
        open: true,
        requestId: prev.requestId + 1,
        view: PRICING_DIALOG_VIEWS.FEATURE,
        key,
        source,
        context: { inUse: !!inUse }
      }))
    },
    []
  )

  /**
   * Limit-Dialog einer Ressource, meist nachdem ein Guard geblockt hat. Jede
   * Aktion außer info zählt als PRICING_LIMIT_BLOCKED.
   *
   * Öffnet nur in enforced. Davor werden Limits nicht durchgesetzt, die
   * Guards blocken nicht, und der Dialog könnte sonst eine Anfrage nach
   * #vertrieb auslösen.
   *
   * @param {'careers'|'seats'|'talentStorage'} resource
   * @param {object} [options]
   * @param {string} [options.source]
   * @param {'activate'|'recover'|'invite'|'duplicate'|'discover'|'selection'|'info'} [options.action]
   * @param {number} [options.count] Anzahl der ausgewählten oder gesperrten
   *   Einträge, für recover und selection
   * @param {() => void} [options.onDeselect] für selection, wählt die
   *   Gesperrten ab
   * @param {number} [options.used] Nutzung aus dem GateResult, falls der
   *   Cache noch nichts hat
   * @param {number} [options.limit] Limit aus dem GateResult
   */
  const openLimitDialog = useCallback(
    (
      resource,
      {
        source = null,
        action = LIMIT_DIALOG_ACTIONS.INFO,
        count,
        onDeselect,
        used,
        limit
      } = {}
    ) => {
      if (!LIMIT_DIALOG_RESOURCES.includes(resource)) {
        console.error(
          new Error(`pricing dialog: unbekannte Ressource ${String(resource)}`)
        )
        return
      }

      const currentEntitlements =
        latest.current.entitlements ?? LEGACY_ENTITLEMENTS

      if (currentEntitlements.mode !== MODES.ENFORCED) {
        console.error(
          new Error(
            `pricing dialog: Limit ${resource} außerhalb von enforced (${currentEntitlements.mode})`
          )
        )
        return
      }

      const normalizedAction = Object.values(LIMIT_DIALOG_ACTIONS).includes(
        action
      )
        ? action
        : LIMIT_DIALOG_ACTIONS.INFO
      const base = trackingBase(currentEntitlements)

      if (normalizedAction !== LIMIT_DIALOG_ACTIONS.INFO) {
        trackEvent(trackEvents.PRICING_LIMIT_BLOCKED, {
          resource,
          action: normalizedAction,
          source,
          count: typeof count === 'number' ? count : null,
          ...base
        })
      }

      trackEvent(trackEvents.PRICING_DIALOG_VIEWED, {
        view: PRICING_DIALOG_VIEWS.LIMIT,
        resource,
        action: normalizedAction,
        source,
        ...base
      })

      setDialog((prev) => ({
        open: true,
        requestId: prev.requestId + 1,
        view: PRICING_DIALOG_VIEWS.LIMIT,
        key: resource,
        source,
        context: {
          action: normalizedAction,
          count: typeof count === 'number' ? count : null,
          onDeselect: typeof onDeselect === 'function' ? onDeselect : null,
          used: typeof used === 'number' ? used : null,
          limit: typeof limit === 'number' ? limit : null
        }
      }))
    },
    []
  )

  const closePricingDialog = useCallback(() => {
    setDialog((prev) => (prev.open ? { ...prev, open: false } : prev))
  }, [])

  const actions = useMemo(
    () => ({ openFeatureDialog, openLimitDialog, closePricingDialog }),
    [openFeatureDialog, openLimitDialog, closePricingDialog]
  )

  return (
    <PricingDialogActionsContext.Provider value={actions}>
      <PricingDialogStateContext.Provider value={dialog}>
        {children}
      </PricingDialogStateContext.Provider>
    </PricingDialogActionsContext.Provider>
  )
}

PricingDialogProvider.propTypes = {
  children: PropTypes.node.isRequired
}

/**
 * @returns {{ openFeatureDialog: Function, openLimitDialog: Function,
 *   closePricingDialog: Function }}
 */
export function usePricingDialog() {
  const context = useContext(PricingDialogActionsContext)

  if (!context) {
    throw new Error(
      'usePricingDialog must be used within a PricingDialogProvider'
    )
  }

  return context
}

// Nur für den Host in components/pricing/pricing-dialog.js
export function usePricingDialogState() {
  const context = useContext(PricingDialogStateContext)

  if (!context) {
    throw new Error(
      'usePricingDialogState must be used within a PricingDialogProvider'
    )
  }

  return context
}
